import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, getToken, setToken } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading, null = logged out
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setReady(true);
      return;
    }
    try {
      const { user } = await api.me();
      setUser(user);
    } catch {
      setToken(null);
      setUser(null);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const applyAuth = (data) => {
    setToken(data.session_token);
    setUser(data.user);
    return data.user;
  };

  const login = async (payload) => applyAuth(await api.login(payload));
  const register = async (payload) => applyAuth(await api.register(payload));
  const adminLogin = async (payload) => applyAuth(await api.adminLogin(payload));
  const logout = async () => {
    try { await api.logout(); } catch {}
    setToken(null);
    setUser(null);
  };

  const isAdmin = !!user && user.role === "superadmin";

  return (
    <AuthCtx.Provider value={{ user, ready, isAdmin, login, register, adminLogin, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
